import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Play, TrendingUp, Eye, Clock, ArrowRight, ChevronRight, Facebook, Twitter, Instagram, Youtube } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { newsAPI } from '../services/api';
import VizyonSektorCarousel from '../components/news/VizyonSektorCarousel';
import HeroSlider from '../components/news/HeroSlider';
import AdSlot from '../components/ads/AdSlot';
import SEO from '../components/seo/SEO';

const MOST_WATCHED_VIEWS = ['160.2K', '132.6K', '96.1K', '78.4K', '65.9K'];

const categoryLabels = {
  'ekonomi': 'Ekonomi',
  'politika': 'Politika',
  'teknoloji': 'Teknoloji',
  'finans': 'Finans',
  'dunya': 'Dünya',
  'vizyon-sektor': 'Vizyon Sektör',
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 60) return `${diffMins} dakika önce`;
  if (diffHours < 24) return `${diffHours} saat önce`;
  return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' });
};

const getYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([^&\s]+)/,
    /(?:youtube\.com\/embed\/)([^?\s]+)/,
    /(?:youtu\.be\/)([^?\s]+)/,
    /(?:youtube\.com\/v\/)([^?\s]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
};

// Video Card Component with inline playback
const HomeVideoCard = ({ video, isFeatured = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoId = getYouTubeId(video.youtube_url);
  const thumbnailUrl = videoId 
    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
    : video.image_url;

  if (isFeatured) {
    return (
      <div className="relative rounded-xl overflow-hidden bg-slate-900">
        <div className="aspect-video relative">
          {isPlaying && videoId ? (
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
              title={video.title}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div 
              className="absolute inset-0 cursor-pointer group"
              onClick={() => setIsPlaying(true)}
            >
              <img
                src={thumbnailUrl}
                alt={video.title}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = video.image_url; }}
              />
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
                <div className="bg-red-600 rounded-full p-5 transition-transform group-hover:scale-110 shadow-2xl">
                  <Play className="w-10 h-10 text-white fill-white" />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="p-5">
          <Link to={`/haber/${video.id}`}>
            <h3 className="text-lg md:text-xl font-bold text-white leading-tight hover:text-[#38BDF8] transition-colors line-clamp-2">
              {video.title}
            </h3>
          </Link>
          <p className="text-gray-400 text-sm mt-2 line-clamp-2">
            {video.body ? video.body.substring(0, 100) + '...' : ''}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative rounded-lg overflow-hidden bg-slate-800">
      <div className="aspect-video relative">
        {isPlaying && videoId ? (
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            title={video.title}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <div 
            className="absolute inset-0 cursor-pointer group"
            onClick={() => setIsPlaying(true)}
          >
            <img
              src={thumbnailUrl}
              alt={video.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              onError={(e) => { e.target.src = video.image_url; }}
            />
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <div className="bg-red-600 rounded-full p-3 transition-transform group-hover:scale-110 shadow-xl">
                <Play className="w-6 h-6 text-white fill-white" />
              </div>
            </div>
            <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
              <h4 className="text-white font-bold text-sm leading-snug line-clamp-2">
                {video.title}
              </h4>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const HomePage = () => {
  const [allNews, setAllNews] = useState([]);
  const [featuredNews, setFeaturedNews] = useState([]);
  const [vizyonVideos, setVizyonVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch featured news for the Hero Slider only
      const featuredResponse = await newsAPI.getAll({ featured: true, limit: 12 });
      if (featuredResponse.data && featuredResponse.data.length > 0) {
        setFeaturedNews(featuredResponse.data);
      }

      // Fetch all news (unfiltered) for category sections & latest grid
      const newsResponse = await newsAPI.getAll({ limit: 30 });
      if (newsResponse.data && newsResponse.data.length > 0) {
        setAllNews(newsResponse.data);
      }

      // Fetch Vizyon Sektör videos (unfiltered — every video appears here)
      const vizyonResponse = await newsAPI.getVizyonSektor();
      if (vizyonResponse.data && vizyonResponse.data.length > 0) {
        setVizyonVideos(vizyonResponse.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter news by category for homepage sections (no featured filtering — every post shows up)
  const { ekonomiNews, finansNews, teknolojiNews } = useMemo(() => ({
    ekonomiNews: allNews.filter((n) => n.category === 'ekonomi'),
    finansNews: allNews.filter((n) => n.category === 'finans'),
    teknolojiNews: allNews.filter((n) => n.category === 'teknoloji'),
  }), [allNews]);

  // Hero slider source: featured if available, otherwise gracefully fall back to latest
  const heroItems = featuredNews.length > 0 ? featuredNews : allNews.slice(0, 12);

  return (
    <Layout>
      <SEO
        title="Haber Türkiye'den - Güncel Haberler, Son Dakika, Vizyon Sektör Programı"
        fullTitle
        description="Türkiye'nin önde gelen ekonomi ve iş dünyası haber portalı. Güncel haberler, piyasa analizleri, Vizyon Sektör röportajları, borsa, dolar, altın ve kripto para takibi."
        keywords="ekonomi, finans, politika, vizyon sektör, borsa, dolar, altın, bitcoin, haber, türkiye"
        image={allNews[0]?.image_url}
      />
      <div className="bg-gray-50 min-h-screen" data-testid="homepage">
        {/* Hero Section - 3 Column Layout */}
        <section className="bg-white border-b" data-testid="hero-section">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Main Featured Slider - Left (only is_featured=true posts) */}
              <div className="lg:col-span-6">
                <HeroSlider items={heroItems.slice(0, 12)} />
              </div>

              {/* Top Stories - Middle */}
              <div className="lg:col-span-4">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-red-600" />
                  <h2 className="text-sm font-bold uppercase tracking-wider text-gray-900">
                    Öne Çıkanlar
                  </h2>
                </div>
                <div className="space-y-4">
                  {allNews.slice(1, 5).map((item) => (
                    <Link key={`top-${item.id}`} to={`/haber/${item.id}`} className="group flex gap-3">
                      <div className="w-24 h-16 flex-shrink-0 rounded overflow-hidden">
                        <img
                          src={item.image_url}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-semibold text-red-600 uppercase">
                          {categoryLabels[item.category]}
                        </span>
                        <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:text-red-600 transition-colors">
                          {item.title}
                        </h3>
                        <span className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(item.created_at)}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Ad Space - Right */}
              <div className="lg:col-span-2 hidden lg:block">
                <div className="sticky top-24">
                  <AdSlot
                    slotKey="hero_sidebar_top"
                    dims={{ w: 300, h: 250 }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Leaderboard Ad */}
        <div className="bg-gray-100 py-4">
          <div className="max-w-7xl mx-auto px-4">
            <AdSlot
              slotKey="leaderboard_top"
              dims={{ w: 728, h: 90 }}
              fallbackLabel="Reklam Alanı - Leaderboard"
              className="max-w-[728px] mx-auto"
            />
          </div>
        </div>

        {/* Vizyon Sektör — Modular Horizontal Carousel */}
        <VizyonSektorCarousel videos={vizyonVideos} />

        {/* Main Content Area with Sidebar */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-8">
                {/* Category: Ekonomi */}
                {ekonomiNews.length > 0 && (
                  <div className="mb-10">
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-2">
                        <div className="w-1 h-6 bg-blue-600 rounded-full" />
                        <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide">Ekonomi</h2>
                      </div>
                      <Link to="/kategori/ekonomi" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center">
                        Tümü <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {ekonomiNews.slice(0, 2).map((item) => (
                        <Link key={`eko-${item.id}`} to={`/haber/${item.id}`} className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                          <div className="aspect-[16/9] overflow-hidden">
                            <img src={item.image_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                          </div>
                          <div className="p-4">
                            <h3 className="font-bold text-gray-900 leading-tight group-hover:text-blue-600 transition-colors line-clamp-2">
                              {item.title}
                            </h3>
                            <p className="text-gray-500 text-sm mt-2 line-clamp-2">{item.body}</p>
                            <span className="text-xs text-gray-400 mt-3 block">{formatDate(item.created_at)}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inline Ad */}
                <AdSlot
                  slotKey="inline_content"
                  dims={{ w: 728, h: 90 }}
                  className="mb-10 max-w-[728px] mx-auto"
                />

                {/* Category: Finans & Teknoloji Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                  {/* Finans */}
                  {finansNews.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-1 h-6 bg-green-600 rounded-full" />
                        <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide">Finans</h2>
                      </div>
                      <div className="space-y-4">
                        {finansNews.slice(0, 3).map((item) => (
                          <Link key={`fin-${item.id}`} to={`/haber/${item.id}`} className="group flex gap-3 bg-white p-3 rounded-lg hover:bg-gray-50 transition-colors">
                            <div className="w-20 h-14 flex-shrink-0 rounded overflow-hidden">
                              <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                            </div>
                            <div>
                              <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:text-green-600">
                                {item.title}
                              </h3>
                              <span className="text-xs text-gray-400">{formatDate(item.created_at)}</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Teknoloji */}
                  {teknolojiNews.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-1 h-6 bg-purple-600 rounded-full" />
                        <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide">Teknoloji</h2>
                      </div>
                      <div className="space-y-4">
                        {teknolojiNews.slice(0, 3).map((item) => (
                          <Link key={`tek-${item.id}`} to={`/haber/${item.id}`} className="group flex gap-3 bg-white p-3 rounded-lg hover:bg-gray-50 transition-colors">
                            <div className="w-20 h-14 flex-shrink-0 rounded overflow-hidden">
                              <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                            </div>
                            <div>
                              <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:text-purple-600">
                                {item.title}
                              </h3>
                              <span className="text-xs text-gray-400">{formatDate(item.created_at)}</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* All Latest News Grid */}
                <div>
                  <div className="flex items-center gap-2 mb-5">
                    <div className="w-1 h-6 bg-red-600 rounded-full" />
                    <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide">Son Haberler</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {allNews.slice(0, 6).map((item) => (
                      <Link key={`son-${item.id}`} to={`/haber/${item.id}`} className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                        <div className="aspect-[16/9] overflow-hidden">
                          <img src={item.image_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        </div>
                        <div className="p-4">
                          <Badge variant="outline" className="mb-2 text-xs">
                            {categoryLabels[item.category]}
                          </Badge>
                          <h3 className="font-bold text-gray-900 leading-tight group-hover:text-red-600 transition-colors line-clamp-2 text-sm">
                            {item.title}
                          </h3>
                          <span className="text-xs text-gray-400 mt-2 block">{formatDate(item.created_at)}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-4">
                <div className="sticky top-24 space-y-6">
                  {/* En Çok İzlenenler — Vizyon Sektör videos with fixed engagement metrics */}
                  <div className="bg-white rounded-lg p-5 shadow-sm" data-testid="most-watched-widget">
                    <div className="flex items-center gap-2 mb-4">
                      <Play className="w-5 h-5 text-red-600 fill-red-600" />
                      <h3 className="font-bold text-gray-900 uppercase text-sm tracking-wide">En Çok İzlenenler</h3>
                    </div>
                    {vizyonVideos.length === 0 ? (
                      <p className="text-sm text-gray-400">Henüz video eklenmemiş.</p>
                    ) : (
                      <div className="space-y-3">
                        {vizyonVideos.slice(0, 5).map((video, idx) => {
                          const viewsFormatted = MOST_WATCHED_VIEWS[idx] || MOST_WATCHED_VIEWS[MOST_WATCHED_VIEWS.length - 1];
                          return (
                            <Link
                              key={`watched-${video.id}`}
                              to={`/haber/${video.id}`}
                              className="group flex gap-3 items-start"
                              data-testid={`most-watched-item-${idx}`}
                            >
                              <span className={`text-2xl font-black flex-shrink-0 w-8 leading-none pt-0.5 ${idx < 3 ? 'text-red-600' : 'text-gray-300'}`}>
                                {idx + 1}
                              </span>
                              <div className="min-w-0">
                                <h4 className="text-sm font-medium text-gray-900 group-hover:text-red-600 transition-colors line-clamp-2 leading-snug">
                                  {video.title}
                                </h4>
                                <span className="text-xs text-gray-400 mt-0.5 inline-flex items-center gap-1">
                                  <Play className="w-3 h-3" /> {viewsFormatted} görüntülenme
                                </span>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Social Media Widget */}
                  <div className="bg-white rounded-lg p-5 shadow-sm">
                    <h3 className="font-bold text-gray-900 uppercase text-sm tracking-wide mb-4">Bizi Takip Edin</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <a href="#" className="flex items-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                        <Facebook className="w-5 h-5" />
                        <span className="text-sm font-medium">Facebook</span>
                      </a>
                      <a href="#" className="flex items-center gap-2 px-4 py-3 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors">
                        <Twitter className="w-5 h-5" />
                        <span className="text-sm font-medium">Twitter</span>
                      </a>
                      <a href="#" className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg hover:from-pink-600 hover:to-purple-700 transition-colors">
                        <Instagram className="w-5 h-5" />
                        <span className="text-sm font-medium">Instagram</span>
                      </a>
                      <a href="#" className="flex items-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                        <Youtube className="w-5 h-5" />
                        <span className="text-sm font-medium">YouTube</span>
                      </a>
                    </div>
                  </div>

                  {/* Ad Space */}
                  <AdSlot
                    slotKey="sidebar_bottom"
                    dims={{ w: 300, h: 250 }}
                  />

                  {/* Tags Widget */}
                  <div className="bg-white rounded-lg p-5 shadow-sm">
                    <h3 className="font-bold text-gray-900 uppercase text-sm tracking-wide mb-4">Popüler Etiketler</h3>
                    <div className="flex flex-wrap gap-2">
                      {['Dolar', 'Altın', 'Borsa', 'Faiz', 'Enflasyon', 'BIST', 'Bitcoin', 'Euro', 'Merkez Bankası', 'Konut'].map((tag) => (
                        <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium hover:bg-red-100 hover:text-red-600 cursor-pointer transition-colors">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default HomePage;
